import { ReactElement, useState, useMemo, useEffect } from 'react';
import { Outlet } from 'react-router-dom';

import {
  useAppDispatch,
  useHeightLayout,
  useRequest,
  useTypePartnerCompany,
  useUIV2State,
} from '~hooks/index';
import { useClientOnMethodMultiple } from '~hooks/useSignalR';
import { getPlansDetails, getPlanSubscription } from '~services/plan';
import { PlanExtended } from '~services/plan/types';
import { AuthSubscription } from '~services/auth/types';
import { getCurrentCapacityTypes } from '~services/capacityTypes';
import { CapacityTypeBase } from '~services/capacityTypes/types';
import { setPlans } from '~store/slices/plans';
import { setSubscription, setCurrentCapacityTypes } from '~store/slices/auth';
import HeaderActionsProvider from '~contexts/HeaderActionsContext';
import { useBannersContext } from '~contexts/Banners';
import { usePopupsContext } from '~contexts/Popups';
import { METHODS_NAME } from '~constants/signalR';

import LoadingFallback from '../LoadingFallback';
import LateralMenu from '../LateralMenu';
import Header from '../Header';
import Footer from '../Footer';
import DialogMobileNotPerformance from '../DialogMobileNotPerformance';
import ProToPlus from '../ProToPlus';
import GlobalBanners from '../GlobalBanners';
import { LoadingContainer, Container, Content, ContentWrapper } from './styles';

const Layout = (): ReactElement => {
  const { header: heightHeader } = useHeightLayout();

  const {
    getIsOpenByType: getIsOpenByTypeBanner,
    handleCloseByType: handleCloseByTypeBanner,
  } = useBannersContext();

  const {
    getIsOpenByType: getIsOpenByTypePopup,
    handleCloseByType: handleCloseByTypePopup,
  } = usePopupsContext();

  const { isCarrier } = useTypePartnerCompany();
  const { isEnabledUIV2 } = useUIV2State();

  const showLateralMenu = useMemo(
    () => (isEnabledUIV2 ? isCarrier : true),
    [isCarrier, isEnabledUIV2],
  );

  useEffect(() => {
    // Hide or show widget Tawk chat
    if (!isCarrier) {
      // @ts-expect-error: Let's ignore a compile error like this unreachable code
      window?.Tawk_API?.hideWidget?.();
    } else {
      // @ts-expect-error: Let's ignore a compile error like this unreachable code
      window?.Tawk_API?.showWidget?.();
    }
  }, [isCarrier]);

  return (
    <Container>
      {showLateralMenu && <LateralMenu />}

      <Content enabledLateralMenu={showLateralMenu}>
        <HeaderActionsProvider>
          <Header />

          <ContentWrapper heightHeader={heightHeader}>
            <Outlet />
          </ContentWrapper>
        </HeaderActionsProvider>
      </Content>

      <Footer />

      <ProToPlus.Banner
        open={getIsOpenByTypeBanner('proToPlus')}
        onClose={() => handleCloseByTypeBanner('proToPlus')}
      />

      <GlobalBanners.FreePremiun
        open={getIsOpenByTypeBanner('freePremiun')}
        onClose={() => handleCloseByTypeBanner('freePremiun')}
      />

      <GlobalBanners.FreePremiunFor30Days
        open={getIsOpenByTypeBanner('freePremiunFor30Days')}
        onClose={() => handleCloseByTypeBanner('freePremiunFor30Days')}
      />

      <ProToPlus.Popup
        open={getIsOpenByTypePopup('proToPlus')}
        onClose={() => handleCloseByTypePopup('proToPlus')}
      />
      <DialogMobileNotPerformance />
    </Container>
  );
};

const LayoutWrapper = (): ReactElement => {
  const dispatch = useAppDispatch();

  const [, loadingPlans] = useRequest({
    request: getPlansDetails,
    payload: null,
    withPostSuccess: (response) => {
      const plansDetails = response.data?.data.results as PlanExtended[];

      dispatch(setPlans(plansDetails));
    },
    withPostFailure: () => {
      console.log('Error load plans details');
    },
  });

  const [loadingCurrentPlanSubscription, setLoadingCurrentPlanSubscription] =
    useState(true);

  const [, , , reloadCurrenPlanSubscription] = useRequest({
    request: getPlanSubscription,
    payload: null,
    withPostSuccess: (response) => {
      const currentPlanSubscription = response.data?.data as AuthSubscription;

      dispatch(setSubscription(currentPlanSubscription));
      setLoadingCurrentPlanSubscription(false);
    },
    withPostFailure: () => {
      console.log('Error load plans subscription');
      setLoadingCurrentPlanSubscription(false);
    },
  });

  const [, loadingCurrentCapacityTypes] = useRequest({
    request: getCurrentCapacityTypes,
    payload: null,
    withPostSuccess: (response) => {
      const capacityTypesDetails = response.data?.data
        .results as CapacityTypeBase[];

      dispatch(setCurrentCapacityTypes(capacityTypesDetails));
    },
    withPostFailure: () => {
      console.log('Error load plans details');
    },
  });

  useClientOnMethodMultiple(METHODS_NAME.ADD_ROUTE, 'Layout', () =>
    reloadCurrenPlanSubscription(null),
  );

  useClientOnMethodMultiple(METHODS_NAME.REFRESH_SUBSCRIPTION, 'Layout', () =>
    reloadCurrenPlanSubscription(null),
  );

  if (
    loadingPlans ||
    loadingCurrentPlanSubscription ||
    loadingCurrentCapacityTypes
  )
    return (
      <LoadingContainer>
        <LoadingFallback />
      </LoadingContainer>
    );

  return <Layout />;
};

export default LayoutWrapper;
